import { Component, OnInit, AfterViewInit, OnDestroy, HostListener, ElementRef, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthState, IdentityAuthService } from './_services/identity-auth.service';
import { ActivatedRoute, ActivationStart, ChildActivationEnd, NavigationEnd, Route, Router } from '@angular/router';
import { MenuService } from './MenuService';
import { UserService } from './_services/user.service';
import { MatSelectChange } from '@angular/material/select';
import { AppConfigService } from './AppConfigService';
import { SiteConfiguration } from './api_identity/api';
import { ApplicationInsightsService } from './_services/application-insights.service';

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: false,
})

export class AppComponent implements OnInit, OnDestroy {
  title = 'B29Intelligence';
  isStandalonePage: boolean = false;
  private configSubscription?: Subscription;

  private sidenavElement?: HTMLElement;
  private sidenavInnerContainer?: HTMLElement;

  constructor(private authService: IdentityAuthService, private router: Router, public userService: UserService,
    private route: ActivatedRoute, private menuService: MenuService, private config: AppConfigService,
    private applicationInsightsService: ApplicationInsightsService) {
    
    // Subscribe to config to get SiteId
    this.configSubscription = this.config.getConfig().subscribe((appConfig) => {
      this.siteId = appConfig.SiteId;
    });

    userService.onUserChanged().subscribe((user) => {
      let sites = this.userService.User?.Sites || [];
      this.siteItems = sites;
    });
  }

  get loginDisplay() {
    return this.userService.User?.UserName != null;
  }

  get userName() {
    return this.userService.User?.UserName;
  }

  private _siteItems: SiteConfiguration[] = [];

  menuItems: Route[] = [];
  get siteItems() {
    return this._siteItems;
  }
  set siteItems(value: SiteConfiguration[]) {
    this._siteItems = value;
  }

  siteId: string = "";

  isSidenavOpen = true;

  toggleSidenav() {
    this.isSidenavOpen = !this.isSidenavOpen;
  }

  IsInRole(role: string) {
    return this.userService.IsInRole(role);
  }

  loadMenuItems() {
    let moduleRoute = this.route.snapshot.children[0].routeConfig?.path;
    if (moduleRoute) {
      this.menuItems = this.menuService.getSecureMenuItems(moduleRoute, this.userService.User?.Roles || []);
    } else {
      this.menuItems = [];
    }
  }

  checkIfStandalonePage(url: string): boolean {
    if (url.includes('.trdp') && (url.includes('/viewer'))) {
      return true;
    } else if (url.includes('.trdp') && (url.includes('/designer'))) { //adding this case broke the fetchOverride in report-designer.component.ts
      return true;
    } else {
      return false;
    }
  }

  async ngOnInit() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Check if we should be in standalone mode
        this.isStandalonePage = this.checkIfStandalonePage(event.url);
        this.loadMenuItems();
        this.setSidenavScrollbarWidth();
      }
    });

    this.menuService.menuItemsChanged.subscribe(() => {
      this.loadMenuItems();
      this.setSidenavScrollbarWidth();
    });

    await this.authService.init();
  }

  login() {
    this.router.navigate(["identity/signin"]);
  }

  async logout() {
    await this.authService.signOut();
    this.router.navigate(["home"]);
  }

  siteChange($event: MatSelectChange) {
    let site = this.siteItems.find(a => a.id == $event.value);
    if (site != null && site.id != this.siteId && site.url != "") {
      let url = new URL(site.url);
      url.searchParams.set("user", this.userService.User?.UserName || "");
      window.location.href = url.toString();
    }
  }

  ngAfterViewInit() {
    this.cacheSidenavElements();
    this.setSidenavScrollbarWidth();
  }

  private cacheSidenavElements() {
    const queried = document.querySelector('.sidenav');
    if (queried instanceof HTMLElement) {
        this.sidenavElement = queried;
    }

    if (this.sidenavElement) {
      this.sidenavInnerContainer = this.sidenavElement.querySelector('.mat-drawer-inner-container') as HTMLElement | undefined;
    }
  }

  ngOnDestroy(): void {
    // Clean up subscription
    if (this.configSubscription) {
      this.configSubscription.unsubscribe();
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.setSidenavScrollbarWidth();
  }

  onSidenavTransitionEnd() {
    window.dispatchEvent(new Event('resize'));
    this.setSidenavScrollbarWidth();
  }

  private setSidenavScrollbarWidth() {
    requestAnimationFrame(() => {
      if (!this.sidenavInnerContainer) {
        this.cacheSidenavElements();
      }
      const innerContainer = this.sidenavInnerContainer;
      if (!innerContainer) return;

      const scrollbarWidth = innerContainer.offsetWidth - innerContainer.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
    });
  }
}
